import { useState, useEffect, useCallback, useRef } from "react";
import type { StateMap } from "../types";
import type { WPClient } from "../lib/wpClient";
import { listSites, lowHangingKeywords, listLhkInserted, listLhkInsertedHistory, markLhkInserted, backfillLhkInserted, recordHistory, type LowHangingPage, type LhkInsertedPage } from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { getAnthropicKey } from "../lib/apiKeys";
import { showConfirm, showAlert } from "../lib/dialog";
import { confirmDeletePopover, infoPopover } from "../lib/confirmPopover";
import { htmlToGutenbergBlocks, collectElementorTextWidgets } from "../lib/fixExecutor";
import { KeywordEditorDialog } from "./KeywordEditorDialog";

interface Props {
  state: StateMap;
  siteUrl?: string;
  siteBrand?: string;
  connected: boolean;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  getClient: () => WPClient | null;
}

interface KwItem { query: string; position?: number; impressions?: number; manual?: boolean; freq?: number; }
interface PageCard {
  fixId: string;
  page: string;          // full URL from GSC
  avgPosition: number;
  impressions: number;
  keywords: KwItem[];
  density: number;       // 1–10 — the card's default keyword frequency (per-keyword freq overrides it)
}

// Effective insertion frequency for a keyword: its own override, else the card's default. 1–10.
const kwFreq = (k: KwItem, card: PageCard): number => Math.max(1, Math.min(10, k.freq ?? card.density));

interface EditorCtx {
  fixId: string; pageType: "pages" | "posts"; pageId: number; page: string; html: string;
  keywords: string[];    // the keywords woven in (recorded to history on publish)
  // Present for Elementor pages: the element tree (with keywords already woven into its text
  // widgets) that gets saved on publish. `html` is then a combined, per-widget-wrapped preview.
  elementor?: { elements: unknown[] };
}

const BRANDED_KEY = "lsg_lhk_branded";
const PAGES_KEY = "lsg_lhk_pages";
const GENERATED_KEY = "lsg_lhk_generated"; // per-card crawled content awaiting/after review
const DEFAULT_DENSITY = 1;

// In-flight actions tracked at MODULE level so switching tabs (which unmounts this component) never
// loses the GSC crawl or a per-card "Crawl & Insert": results are written straight to localStorage
// and the mount effects re-attach to restore the spinners and apply results (and never double-run).
type LhkCrawlResult = { ok: boolean; count: number; error?: string };
let lhkCrawlInFlight: Promise<LhkCrawlResult> | null = null;
const lhkInsertRuns = new Map<string, Promise<void>>(); // fixId → in-flight "Crawl & Insert"

// Merge one card's generated content into the localStorage store directly (unmount-safe — never
// relies on setState → useEffect, which no-ops after the component unmounts).
function writeGenerated(fixId: string, ctx: EditorCtx): void {
  try {
    const cur = JSON.parse(localStorage.getItem(GENERATED_KEY) ?? "{}") as Record<string, EditorCtx>;
    cur[fixId] = ctx;
    localStorage.setItem(GENERATED_KEY, JSON.stringify(cur));
  } catch { /* ignore */ }
}
const readGenerated = (): Record<string, EditorCtx> => { try { return JSON.parse(localStorage.getItem(GENERATED_KEY) ?? "{}") as Record<string, EditorCtx>; } catch { return {}; } };

// Shown (once per card, per session) when the user starts editing a page that has ALREADY had
// keywords published to it. Approve (a fresh pass, e.g. the URL reappearing after a re-crawl) gets
// the short reminder; Regenerate (re-running content generated this session) gets the fuller
// additive-rounds warning.
const REINSERT_MSG = "This page has already had low-hanging keywords inserted into it via Local SEO Garage. Are you sure you want to add low-hanging keywords again?";
const REOPTIMIZE_MSG = "This page already had low-hanging keywords published to it. Regenerating and publishing again will add another round of keywords on top. It would raise the keyword frequency of each keyword even more if you re-add the same keywords over again (unless you are adding new keywords that were not added before). Would you like to continue?";

// Normalize a page URL so the "already inserted" record matches regardless of query
// string, fragment, trailing slash, or case.
function normalizePage(page: string): string {
  return page.toLowerCase().replace(/[#?].*$/, "").replace(/\/+$/, "");
}

// Merge the tool's DB history with the durable on-page _lsg_lhk_optimized scan (plugin v1.24.0+) into
// one row per physical page — no duplicates. Matching is by WordPress page ID first (survives slug
// changes: the DB row records the id at insertion), then by normalized URL for legacy rows that have
// no id yet. A matched row shows the page's CURRENT permalink/title/type from the scan while keeping
// the DB's recorded keywords + edited date; a page seen ONLY in the scan (DB record lost) is added at
// its current URL; a DB row with no id and no scan match (older plugin / page deleted) is kept as-is.
// Sorted newest-first.
type LhkMetaPage = { id: number; title: string; link: string; type: "page" | "post"; optimized?: string };
function mergeLhkHistory(dbPages: LhkInsertedPage[], metaPages: LhkMetaPage[]): LhkInsertedPage[] {
  // 1) Collapse DB rows that point to the SAME page id (the same page optimized at two URLs across a
  //    slug change → two rows) into one — newest wins, keywords unioned. Rows with no id stay by URL.
  const dbById = new Map<number, LhkInsertedPage>();
  const dbNoId: LhkInsertedPage[] = [];
  for (const r of dbPages) {
    if (r.pageId == null) { dbNoId.push({ ...r }); continue; }
    const prev = dbById.get(r.pageId);
    if (!prev) { dbById.set(r.pageId, { ...r }); continue; }
    const newer = (r.editedAt || "") >= (prev.editedAt || "") ? r : prev;
    const older = newer === r ? prev : r;
    dbById.set(r.pageId, { ...newer, keywords: [...new Set([...newer.keywords, ...older.keywords])] });
  }

  const metaById = new Map<number, LhkMetaPage>();
  const metaByUrl = new Map<string, LhkMetaPage>();
  for (const m of metaPages) { metaById.set(m.id, m); metaByUrl.set(normalizePage(m.link), m); }
  const consumed = new Set<number>(); // scan rows already folded into a DB row
  const out: LhkInsertedPage[] = [];

  // 2) DB rows WITH an id → reconcile to the scan by identity (current URL wins).
  for (const r of dbById.values()) {
    const m = metaById.get(r.pageId!);
    if (m) consumed.add(m.id);
    out.push(m
      ? { url: m.link, title: r.title || m.title, type: r.type || m.type, editedAt: r.editedAt || m.optimized || "", keywords: r.keywords, pageId: r.pageId }
      : r);
  }

  // 3) DB rows WITHOUT an id → match the scan by normalized URL. If that page is already represented
  //    via an id-matched row, drop this stale URL row; otherwise keep it (adopting the scan's id).
  for (const r of dbNoId) {
    const m = metaByUrl.get(normalizePage(r.url));
    if (m) {
      if (consumed.has(m.id)) continue; // same page already shown via its id — no duplicate
      consumed.add(m.id);
      out.push({ url: m.link, title: r.title || m.title, type: r.type || m.type, editedAt: r.editedAt || m.optimized || "", keywords: r.keywords, pageId: m.id });
    } else {
      out.push(r);
    }
  }

  // 4) Pages seen only in the on-page scan (no DB record) → add at their current URL.
  for (const m of metaPages) {
    if (consumed.has(m.id)) continue;
    out.push({ url: m.link, title: m.title, type: m.type, editedAt: m.optimized ?? "", keywords: [], pageId: m.id });
  }

  return out.sort((a, b) => (b.editedAt || "").localeCompare(a.editedAt || ""));
}

// Remove the review-only keyword highlight wrapper, keeping the inner text, before publishing.
function stripHighlights(html: string): string {
  return html.replace(/<mark class="lhk-hl">/g, "").replace(/<\/mark>/g, "");
}

function urlPath(url: string): string {
  try { return new URL(url).pathname || "/"; } catch { return url; }
}
function urlToSlug(url: string): string {
  try {
    const parts = new URL(url).pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
    return parts.length ? parts[parts.length - 1] : "";
  } catch { return ""; }
}
function fixIdFor(url: string): string {
  return "lhk-" + urlPath(url).replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();
}
// Resolve a GSC/history URL → its WordPress page or post (id + type). Prefers a slug
// lookup (accurate for both pages and posts), falling back to a URL match (assumes page).
async function resolveWpPage(client: WPClient, url: string): Promise<{ pageId: number; pageType: "pages" | "posts" } | null> {
  const slug = urlToSlug(url);
  if (slug) {
    const pg = await client.getPageBySlug(slug);
    if (pg) return { pageId: pg.id, pageType: "pages" };
    const post = await client.getPostBySlug(slug);
    if (post) return { pageId: post.id, pageType: "posts" };
  }
  const id = await client.getPageIdByUrl(url);
  return id === null ? null : { pageId: id, pageType: "pages" };
}
// Deep link into the Google Search Console performance report for a single page: 6-month range
// with Clicks, Impressions, and Average Position selected, filtered to that URL. GSC's UI URL
// params are undocumented but stable in practice; if Google changes them it degrades to opening
// the property's performance report (the user can then adjust filters).
function gscPerformanceUrl(property: string, pageUrl: string): string {
  const q = [
    `resource_id=${encodeURIComponent(property)}`,
    "num_of_months=6",
    `metrics=${encodeURIComponent("CLICKS,IMPRESSIONS,POSITION")}`,
    `page=${encodeURIComponent("*" + pageUrl)}`, // "*" = page-URL filter
  ].join("&");
  return `https://search.google.com/search-console/performance/search-analytics?${q}`;
}
function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export function LowHangingKw(props: Props) {
  const [branded, setBranded] = useState(() => { try { return localStorage.getItem(BRANDED_KEY) ?? ""; } catch { return ""; } });
  const [pages, setPages] = useState<PageCard[]>(() => {
    try { return JSON.parse(localStorage.getItem(PAGES_KEY) ?? "[]") as PageCard[]; } catch { return []; }
  });
  const [crawling, setCrawling] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [gscConnected, setGscConnected] = useState(false);
  const [gscProperty, setGscProperty] = useState(""); // e.g. "sc-domain:example.com" — for GSC deep links
  const [busy, setBusy] = useState<Set<string>>(new Set());      // fixIds currently crawling (concurrent)
  const [progress, setProgress] = useState<Record<string, string>>({}); // per-card crawl stage text
  const [generated, setGenerated] = useState<Record<string, EditorCtx>>(() => { // completed content per card
    try { return JSON.parse(localStorage.getItem(GENERATED_KEY) ?? "{}") as Record<string, EditorCtx>; } catch { return {}; }
  });
  const [editor, setEditor] = useState<EditorCtx | null>(null);  // which card's content is open in the dialog
  const [addKw, setAddKw] = useState<Record<string, string>>({}); // per-card "add keyword" input text
  const [inserted, setInserted] = useState<Set<string>>(new Set()); // pages already inserted into (normalized URLs)
  const [history, setHistory] = useState<LhkInsertedPage[]>([]);     // History — Optimized Pages
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => { try { localStorage.setItem(BRANDED_KEY, branded); } catch { /* ignore */ } }, [branded]);
  useEffect(() => { try { localStorage.setItem(PAGES_KEY, JSON.stringify(pages)); } catch { /* ignore */ } }, [pages]);
  useEffect(() => { try { localStorage.setItem(GENERATED_KEY, JSON.stringify(generated)); } catch { /* ignore */ } }, [generated]);

  // Resolve the active site to its saved-site id + GSC connection status.
  useEffect(() => {
    let alive = true;
    const key = props.siteUrl ? siteKey(props.siteUrl) : "";
    if (!key) { setSiteId(null); setGscConnected(false); setGscProperty(""); return; }
    listSites().then(sites => {
      if (!alive) return;
      const match = sites.find(s => siteKey(s.siteUrl) === key);
      setSiteId(match?.id ?? null);
      setGscConnected(!!match?.gscConnected);
      setGscProperty(match?.gscProperty ?? "");
    }).catch(() => { if (alive) { setSiteId(null); setGscConnected(false); setGscProperty(""); } });
    return () => { alive = false; };
  }, [props.siteUrl]);

  // Load the set of pages already inserted into (server-side, per user + site).
  useEffect(() => {
    let alive = true;
    const key = props.siteUrl ? siteKey(props.siteUrl) : "";
    if (!key) { setInserted(new Set()); return; }
    listLhkInserted(key).then(pages => { if (alive) setInserted(new Set(pages)); }).catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [props.siteUrl]);

  const runCrawl = useCallback(async () => {
    if (crawling || lhkCrawlInFlight) return;
    const key = props.siteUrl ? siteKey(props.siteUrl) : "";
    if (!key) {
      void showAlert("Select a website first (top of the page), then Run Crawl.", { title: "No Website Selected" });
      return;
    }
    setCrawling(true);
    setStatus({ tone: "info", text: "Verifying the Google Search Console connection…" });

    // Validate the GSC connection LIVE at click time — the local gscConnected flag is only a
    // mount-time snapshot, so re-check against the server before crawling.
    let liveSiteId: string;
    try {
      const sites = await listSites();
      const match = sites.find(s => siteKey(s.siteUrl) === key);
      setSiteId(match?.id ?? null);
      setGscConnected(!!match?.gscConnected);
      if (!match || !match.id || !match.gscConnected) {
        setStatus(null);
        setCrawling(false);
        void showAlert("Connect this website to Google Search Console first — open Settings → Saved Websites and connect a property.", { title: "Google Search Console Not Connected" });
        return;
      }
      liveSiteId = match.id;
    } catch {
      setStatus(null);
      setCrawling(false);
      void showAlert("Couldn't verify the Google Search Console connection. Please check your connection and try again.", { title: "Connection Check Failed" });
      return;
    }

    setStatus({ tone: "info", text: "Querying Google Search Console for the last 90 days…" });
    // Run the crawl as a module-level promise so it survives a tab switch; write the resulting cards
    // straight to localStorage (unmount-safe) and reset the per-card generated store.
    lhkCrawlInFlight = (async () => {
      const brandedList = branded.split(/[\n,]+/).map(b => b.trim()).filter(Boolean);
      const result = await lowHangingKeywords(liveSiteId, brandedList, getAnthropicKey() || undefined);
      const cards: PageCard[] = result.map(p => ({
        fixId: fixIdFor(p.page), page: p.page, avgPosition: p.avgPosition, impressions: p.impressions, keywords: p.keywords, density: DEFAULT_DENSITY,
      }));
      try { localStorage.setItem(PAGES_KEY, JSON.stringify(cards)); } catch { /* ignore */ }
      try { localStorage.setItem(GENERATED_KEY, "{}"); } catch { /* ignore */ } // fresh crawl → drop prior generated content
      return { ok: true, count: cards.length };
    })();
    void lhkCrawlInFlight.catch(() => {}).finally(() => { lhkCrawlInFlight = null; });
    try {
      const r = await lhkCrawlInFlight;
      try { setPages(JSON.parse(localStorage.getItem(PAGES_KEY) ?? "[]") as PageCard[]); } catch { /* ignore */ }
      setGenerated({});
      setProgress({});
      reoptimizeAckedRef.current.clear(); // re-warn about already-published pages after a new crawl
      setStatus(r.count
        ? { tone: "info", text: `Found ${r.count} page${r.count === 1 ? "" : "s"} with low-hanging keyword opportunities.` }
        : { tone: "info", text: "No pages met the criteria (avg position 35 or better with keywords ranking 5–20)." });
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Crawl failed." });
    } finally {
      setCrawling(false);
    }
  }, [crawling, branded, props.siteUrl]);

  // Re-attach to a GSC crawl still in flight from before a tab switch: restore the spinner and apply
  // the cards (already written to localStorage) when it finishes.
  useEffect(() => {
    if (!lhkCrawlInFlight) return;
    let alive = true;
    setCrawling(true);
    setStatus({ tone: "info", text: "Querying Google Search Console for the last 90 days…" });
    lhkCrawlInFlight
      .then(r => {
        if (!alive) return;
        try { setPages(JSON.parse(localStorage.getItem(PAGES_KEY) ?? "[]") as PageCard[]); } catch { /* ignore */ }
        setGenerated(readGenerated());
        setStatus(r.count
          ? { tone: "info", text: `Found ${r.count} page${r.count === 1 ? "" : "s"} with low-hanging keyword opportunities.` }
          : { tone: "info", text: "No pages met the criteria (avg position 35 or better with keywords ranking 5–20)." });
      })
      .catch(e => { if (alive) setStatus({ tone: "err", text: e instanceof Error ? e.message : "Crawl failed." }); })
      .finally(() => { if (alive) setCrawling(false); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeKeyword = useCallback((fixId: string, query: string) => {
    setPages(prev => prev.map(c => c.fixId === fixId ? { ...c, keywords: c.keywords.filter(k => k.query !== query) } : c));
  }, []);
  const addKeyword = useCallback((fixId: string, raw: string) => {
    const query = raw.trim();
    if (!query) return;
    setPages(prev => prev.map(c => {
      if (c.fixId !== fixId) return c;
      if (c.keywords.some(k => k.query.toLowerCase() === query.toLowerCase())) return c; // no duplicates
      return { ...c, keywords: [...c.keywords, { query, manual: true }] };
    }));
    setAddKw(prev => ({ ...prev, [fixId]: "" }));
  }, []);
  const setDensity = useCallback((fixId: string, value: number) => {
    const v = Math.max(1, Math.min(10, Math.round(value || 1)));
    setPages(prev => prev.map(c => c.fixId === fixId ? { ...c, density: v } : c));
  }, []);
  // Per-keyword frequency override (1–10). Keywords without one follow the card's main frequency.
  const setKeywordFreq = useCallback((fixId: string, query: string, value: number) => {
    const v = Math.max(1, Math.min(10, Math.round(value || 1)));
    setPages(prev => prev.map(c => c.fixId === fixId
      ? { ...c, keywords: c.keywords.map(k => k.query === query ? { ...k, freq: v } : k) }
      : c));
  }, []);
  const removeCard = useCallback((fixId: string) => {
    setPages(prev => prev.filter(c => c.fixId !== fixId));
    setGenerated(prev => { const n = { ...prev }; delete n[fixId]; return n; });
  }, []);

  // skipConfirm: true when triggered by the in-place "Regenerate" popover (which already
  // confirmed), so we don't also pop the modal confirm.
  const handleApprove = useCallback(async (card: PageCard, skipConfirm = false) => {
    if (busy.has(card.fixId) || lhkInsertRuns.has(card.fixId)) return;
    const client = props.getClient();
    if (!client) { void showAlert("Connect to WordPress first (top of the page) so the content can be edited.", { title: "WordPress Not Connected" }); return; }
    if (!card.keywords.length) { void showAlert("This page has no keywords left to insert.", { title: "No Keywords" }); return; }
    if (!getAnthropicKey()) { void showAlert("Add your Anthropic API key in ⚙ Settings → API Keys to enable keyword insertion.", { title: "API Key Required" }); return; }

    // The already-published (over-optimization) warning is handled at the button, in place. Here
    // we only confirm a normal first-time insertion. skipConfirm is set for Regenerate and for the
    // already-acknowledged over-optimization path.
    if (!skipConfirm) {
      const confirmed = await showConfirm(
        `Insert ${card.keywords.length} keyword(s) into:\n${urlPath(card.page)}\n\nEach keyword is woven in at the frequency set next to it in the list (1–10 times). The page content will be crawled and rewritten with the keywords woven in naturally. You'll review the result in an editor before anything is published. Continue?`,
        { title: "Insert Low-Hanging Keywords", confirmLabel: "Crawl & Insert" },
      );
      if (!confirmed) return;
    }

    setBusy(prev => new Set(prev).add(card.fixId));
    // Run the insert as a module-tracked promise so a tab switch mid-insert doesn't lose the spinner
    // or the generated result (which is written straight to localStorage below).
    const runInsert = (async () => {
    try {
      // Resolve the GSC URL → a WordPress page or post.
      const slug = urlToSlug(card.page);
      let pageType: "pages" | "posts" = "pages";
      let pageId: number | null = null;
      if (slug) {
        const pg = await client.getPageBySlug(slug);
        if (pg) { pageId = pg.id; pageType = "pages"; }
        else {
          const post = await client.getPostBySlug(slug);
          if (post) { pageId = post.id; pageType = "posts"; }
        }
      }
      if (pageId === null) pageId = await client.getPageIdByUrl(card.page);
      if (pageId === null) {
        void showAlert(`Couldn't find a WordPress page or post matching ${urlPath(card.page)}. It may be a custom URL, archive, or no longer exist.`, { title: "Page Not Found", tone: "danger" });
        return;
      }

      // Route by THIS page's builder. Elementor stores body text in text-editor widgets
      // inside its element tree (via the LSG helper plugin), so we rewrite those widgets.
      const model = await client.getBuilderModel(pageType, pageId);
      if (model.builder === "elementor") {
        if (!model.viaHelper || !Array.isArray(model.elements)) {
          void showAlert(`${urlPath(card.page)} is built with Elementor. Install/activate the Local SEO Garage (LSG) plugin (Settings → WP Integration) so the tool can read and edit Elementor page content.`, { title: "Elementor — Plugin Required", tone: "info" });
          return;
        }
        const elements = model.elements as unknown[];
        const widgets = collectElementorTextWidgets(elements);
        if (!widgets.length) {
          void showAlert(`${urlPath(card.page)} is an Elementor page but has no editable text widgets to weave keywords into.`, { title: "No Editable Content", tone: "info" });
          return;
        }
        setProgress(prev => ({ ...prev, [card.fixId]: "Integrating low-hanging keywords naturally into existing content.." }));
        const res = await fetch("/api/insert-keywords", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: widgets.map(w => w.settings.editor),
            keywords: card.keywords.map(k => k.query),
            density: card.density,
            keywordFreqs: card.keywords.map(k => ({ keyword: k.query, frequency: kwFreq(k, card) })),
            anthropicKey: getAnthropicKey(),
            brand: props.siteBrand,
            pageUrl: card.page,
          }),
        });
        const data = await res.json() as { ok: boolean; contents?: string[]; error?: string };
        if (!data.ok || !data.contents || data.contents.length !== widgets.length) {
          void showAlert(data.error ?? "Keyword insertion failed.", { title: "Insertion Failed", tone: "danger" });
          return;
        }
        // Write the highlighted HTML back into the (live) widget refs, and build a combined
        // preview where each widget is wrapped so edits can be mapped back on publish.
        widgets.forEach((w, i) => { w.settings.editor = data.contents![i]; });
        const combined = widgets.map((w, i) => `<div data-lhk-w="${i}">${w.settings.editor}</div>`).join("\n");
        writeGenerated(card.fixId, { fixId: card.fixId, pageType, pageId, page: card.page, html: combined, keywords: card.keywords.map(k => k.query), elementor: { elements } });
        return;
      }
      const content = model.content ?? await client.getRawContent(pageType, pageId);
      if (!content || !content.trim()) {
        void showAlert(`Couldn't read editable content for ${urlPath(card.page)} (it may use a page builder or have empty body content).`, { title: "No Editable Content", tone: "danger" });
        return;
      }

      setProgress(prev => ({ ...prev, [card.fixId]: "Integrating low-hanging keywords naturally into existing content.." }));
      const res = await fetch("/api/insert-keywords", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content,
          keywords: card.keywords.map(k => k.query),
          density: card.density,
          anthropicKey: getAnthropicKey(),
          brand: props.siteBrand,
          pageUrl: card.page,
        }),
      });
      const data = await res.json() as { ok: boolean; content?: string; error?: string };
      if (!data.ok || !data.content) {
        void showAlert(data.error ?? "Keyword insertion failed.", { title: "Insertion Failed", tone: "danger" });
        return;
      }
      // Don't auto-open the editor — stash the content so the card shows a "View Page
      // Content" button. This lets several crawls run at once and be reviewed at will.
      writeGenerated(card.fixId, { fixId: card.fixId, pageType, pageId, page: card.page, html: data.content!, keywords: card.keywords.map(k => k.query) });
    } catch (e) {
      void showAlert(e instanceof Error ? e.message : "Something went wrong.", { title: "Error", tone: "danger" });
    }
    })();
    lhkInsertRuns.set(card.fixId, runInsert);
    void runInsert.finally(() => lhkInsertRuns.delete(card.fixId));
    try { await runInsert; }
    finally {
      // Reload generated content from localStorage (written unmount-safely inside the run) and clear
      // this card's spinner.
      setGenerated(readGenerated());
      setBusy(prev => { const n = new Set(prev); n.delete(card.fixId); return n; });
      setProgress(prev => { const n = { ...prev }; delete n[card.fixId]; return n; });
    }
  }, [busy, props]);

  // Re-attach to any per-card "Crawl & Insert" runs still in flight after returning to this tab:
  // restore each card's spinner and apply the generated content when it finishes.
  useEffect(() => {
    const attach = pages.filter(c => lhkInsertRuns.has(c.fixId) && !busy.has(c.fixId));
    if (!attach.length) return;
    let alive = true;
    setBusy(prev => { const n = new Set(prev); attach.forEach(c => n.add(c.fixId)); return n; });
    for (const c of attach) {
      const p = lhkInsertRuns.get(c.fixId);
      if (!p) continue;
      p.then(() => { if (alive) setGenerated(readGenerated()); })
        .catch(() => {})
        .finally(() => { if (alive) { setBusy(prev => { const n = new Set(prev); n.delete(c.fixId); return n; }); setProgress(prev => { const n = { ...prev }; delete n[c.fixId]; return n; }); } });
    }
    return () => { alive = false; };
  }, [pages]); // eslint-disable-line react-hooks/exhaustive-deps

  // History — Optimized Pages: every page this tool has edited (per user + site).
  // Rows optimized before title/type were recorded are backfilled here: when WordPress is
  // connected, each such page is resolved to its WP title + type and written back (without
  // changing the original edited date), then the list is refreshed. Per-session ref guards
  // against re-attempting pages that can't be resolved (deleted / custom URLs).
  const backfilledRef = useRef<Set<string>>(new Set());
  // fixIds where the user has acknowledged re-optimizing an already-published page this session,
  // so the over-optimization warning asks only once (via Approve or Regenerate). Cleared on a new crawl.
  const reoptimizeAckedRef = useRef<Set<string>>(new Set());
  const loadHistory = useCallback(async () => {
    const sk = props.siteUrl ? siteKey(props.siteUrl) : "";
    if (!sk) { setHistory([]); setHistoryLoaded(false); return; }
    setHistoryLoading(true);
    try {
      const client = props.getClient();
      // Durable on-page source (LSG plugin v1.24.0+): pages carrying the _lsg_lhk_optimized meta with
      // their CURRENT permalinks, so re-slugged pages still resolve. Merged with the tool's own
      // URL-keyed DB history (which also carries the inserted keywords). Empty when not connected or
      // on an older plugin — the DB history then stands alone, exactly as before.
      const metaPages = client ? await client.listLhkOptimizedPages().catch(() => []) : [];

      const dbPages = await listLhkInsertedHistory(sk);
      setHistory(mergeLhkHistory(dbPages, metaPages));
      setHistoryLoaded(true);

      // Backfill any row still missing its title/type OR its WP page id (older records). Recording
      // the id upgrades a legacy row so it reconciles to the on-page meta by identity from then on.
      const missing = dbPages.filter(r => (!r.title || !r.type || r.pageId == null) && !backfilledRef.current.has(r.url));
      if (client && missing.length) {
        let changed = false;
        for (const r of missing) {
          backfilledRef.current.add(r.url);
          try {
            const resolved = await resolveWpPage(client, r.url);
            if (!resolved) continue;
            const title = r.title || await client.getTitle(resolved.pageType, resolved.pageId);
            await backfillLhkInserted(sk, r.url, title, r.type || resolved.pageType, resolved.pageId);
            changed = true;
          } catch { /* best effort — leave this row for a later attempt */ }
        }
        if (changed) setHistory(mergeLhkHistory(await listLhkInsertedHistory(sk), metaPages));
      }
    } catch { /* leave previous list */ }
    finally { setHistoryLoading(false); }
    // getClient reads a live ref, so keying only on siteUrl keeps this stable while still
    // reading the current WP client; props.connected below re-triggers it once WP connects.
  }, [props.siteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void loadHistory(); }, [loadHistory, props.connected]);

  // Manual Refresh: require a WordPress connection (needed to resolve page titles/types),
  // otherwise tell the user to connect first and then refresh.
  const handleRefresh = useCallback(() => {
    if (!props.connected || !props.getClient()) {
      void showAlert("Connect to WordPress first (top of the page), then click Refresh to load the page titles and types.", { title: "WordPress Not Connected" });
      return;
    }
    void loadHistory();
  }, [props, loadHistory]);

  // Publish the reviewed content: strip highlights, format for the site's editor, write it back.
  const publishEdited = useCallback(async (editedHtml: string) => {
    if (!editor) return;
    const client = props.getClient();
    if (!client) throw new Error("WordPress connection lost — reconnect and try again.");

    if (editor.elementor) {
      // Elementor: start from the AI-rewritten tree (highlights stripped), then overlay any
      // edits the user made in the review editor — each wrapped block maps to a text widget by
      // index. A widget whose block is missing/unparseable keeps its AI-rewritten value, so a
      // broken edit can never blank a widget or damage the layout.
      const tree = editor.elementor.elements;
      const widgets = collectElementorTextWidgets(tree);
      widgets.forEach(w => { w.settings.editor = stripHighlights(w.settings.editor); });
      const doc = new DOMParser().parseFromString(editedHtml, "text/html");
      doc.querySelectorAll<HTMLElement>("[data-lhk-w]").forEach(block => {
        const i = parseInt(block.getAttribute("data-lhk-w") || "", 10);
        if (!Number.isNaN(i) && widgets[i]) widgets[i].settings.editor = stripHighlights(block.innerHTML);
      });
      const r = await client.saveElementorElements(editor.pageId, tree);
      if (!r.success) throw new Error((r.data as { message?: string })?.message || (r.error as string) || "WordPress rejected the Elementor update.");
    } else {
      const clean = stripHighlights(editedHtml);
      const editorType = await client.detectEditor();
      const body = editorType === "block" ? htmlToGutenbergBlocks(clean) : clean;
      const r = await client.updateContent(editor.pageType, editor.pageId, body);
      if (!r.success) throw new Error((r.error as string) || "WordPress rejected the update.");
    }
    // Durable on-page marker: stamp the ISO time the tool wove keywords into this page. Unlike the
    // server-side history record below (keyed by URL), this lives on the page itself — so it survives
    // URL/slug changes and is visible in WordPress. Best-effort; needs the plugin's _lsg_lhk_optimized
    // meta (older plugin versions silently drop it — the server-side history still records the edit).
    try { await client.markLhkOptimized(editor.pageId, editor.pageType); } catch { /* non-fatal */ }
    // Remember (server-side) so a future insertion warns about over-optimizing, and so the
    // page shows in the History — Optimized Pages list with its title + type + edited date.
    const sk = props.siteUrl ? siteKey(props.siteUrl) : "";
    const np = normalizePage(editor.page);
    if (sk) {
      let title = "";
      try { title = await client.getTitle(editor.pageType, editor.pageId); } catch { /* title is best-effort */ }
      try { await markLhkInserted(sk, np, title, editor.pageType, editor.keywords, editor.pageId); } catch { /* non-fatal */ }
      void recordHistory({ action: "lhk_added", url: np, siteKey: sk, pageId: editor.pageId });
      setInserted(prev => new Set(prev).add(np));
      void loadHistory();
    }
    props.onDone(editor.fixId, true);
    setEditor(null);
    void showAlert(`Selected low-hanging keywords have been inserted into the existing body content on ${urlPath(editor.page)} in WordPress.`, { title: "Published" });
  }, [editor, props, loadHistory]);

  const notReady = !props.connected || !siteId || !gscConnected;

  // Theme-aware palette (reuses the dark values already used across the other tabs) so the
  // inline-styled cards/chips/text match the rest of the app in dark mode. Reads the current
  // theme on render; App re-renders the tree on theme toggle, so this stays in sync.
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const cardBg     = isDark ? "#16223a" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const hdrBg      = isDark ? "#26375a" : "#def5e7";
  const hdrBorder  = isDark ? "rgba(255,255,255,.10)" : "#c7ecd6";
  const ink        = isDark ? "#e6edf6" : "#0f172a";
  const ink2       = isDark ? "#93a4c0" : "#64748b";
  const inkMuted   = isDark ? "#7c8aa0" : "#94a3b8";
  const link       = isDark ? "#7dd3fc" : "#2563eb";
  const red        = isDark ? "#f87171" : "#dc2626";
  const boxBg      = isDark ? "rgba(255,255,255,.04)" : "#f8fafc";
  const boxBorder  = isDark ? "rgba(255,255,255,.12)" : "#e2e8f0";
  const chipBg     = isDark ? "#243149" : "#fff";
  const chipBorder = isDark ? "rgba(255,255,255,.14)" : "#cbd5e1";

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      <div style={{ padding: "0 9px", marginBottom: 20 }}>
        <div className="cd-hd-brand" style={{ fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: "#f97316", letterSpacing: ".7px" }}>Low-Hanging Keyword Insertion (Google Search Console)</div>
        <div className="cd-help" style={{ fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 680 }}>
          Using your connected <strong>Google Search Console</strong> data, this finds pages with a total average position of 35 or better that have
          non-branded keywords sitting just off page one (positions 5–20, greater than or equal to 3 impressions in 90 days), and weaves those keywords into the page content to lift rankings.
          The <strong>Keyword Frequency</strong> field on each page sets the default number of times each keyword (or a close variation) is added into that
          page&rsquo;s existing content, and you can override the frequency for any individual keyword using the small box next to it in the list — 1 to 10 each. Keep it modest and be cautious of over-optimizing.
          On standard (non-Elementor) pages, keywords may also be woven into existing headings; on Elementor-built pages, only body text is edited — headings will not have keywords inserted into them.
        </div>
      </div>

      {/* Controls: branded-exclude field with Run Crawl right-aligned below it */}
      <div style={{ padding: "0 9px", marginBottom: 22 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: ink2, fontFamily: "'Inter','Mulish',sans-serif", marginBottom: 4 }}>
          Enter Brand Variations to Exclude (comma delimited)
        </label>
        <textarea
          value={branded}
          onChange={e => setBranded(e.target.value)}
          placeholder="Brand name and variations (one per line or comma-separated) — these are excluded from results"
          rows={2}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical", padding: "8px 11px", border: "1px solid #cbd5e1", borderRadius: 0, fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none" }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 10 }}>
          {notReady && (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: link }}>
              {!props.connected ? "Connect to WordPress to Run Crawl." : "Connect Google Search Console to Run Crawl."}
            </span>
          )}
          <button
            className="run-crawl-btn"
            onClick={() => void runCrawl()}
            disabled={crawling || notReady}
            style={{
              padding: "10px 18px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif",
              fontSize: 13, fontWeight: 700, color: "#fff", cursor: crawling || notReady ? "default" : "pointer",
              background: crawling || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap",
              transition: "background .3s ease, box-shadow .15s, transform .15s",
            }}
          >
            {crawling ? "Crawling…" : "Run Crawl"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 12.5, color: status.tone === "err" ? red : link }}>{status.text}</div>
      )}

      {/* Page cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "0 9px" }}>
        {pages.map(card => {
          const st = props.state[card.fixId];
          const done = st === "done";
          const ignored = st === "ignored";
          return (
            <div key={card.fixId} style={{ border: `1px solid ${cardBorder}`, borderRadius: 10, background: cardBg, overflow: "visible", opacity: done ? 0.6 : ignored ? 0.5 : 1, transition: "opacity .2s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, background: hdrBg, borderBottom: `1px solid ${hdrBorder}`, padding: "12px 16px", borderRadius: "10px 10px 0 0" }}>
                <div style={{ minWidth: 0 }}>
                  {st && (
                    <div style={{ fontSize: 9.5, fontWeight: 700, marginBottom: 5, color: done ? (isDark ? "#4ade80" : "#166534") : ignored ? ink2 : (isDark ? "#c4b5fd" : "#6d28d9") }}>
                      {done ? "✓ Complete" : ignored ? "— Ignored" : "⏳ Approved"}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <a href={card.page} target="_blank" rel="noreferrer" style={{ fontSize: 13.5, fontWeight: 500, color: link, textDecoration: "none", wordBreak: "break-all" }}>{urlPath(card.page)}</a>
                    {inserted.has(normalizePage(card.page)) && (
                      <span title="This page has been previously updated with low-hanging keywords." style={{ fontSize: 10, fontWeight: 600, color: isDark ? "#fcd34d" : "#92400e", background: isDark ? "rgba(245,158,11,.14)" : "#fef3c7", border: `1px solid ${isDark ? "rgba(245,158,11,.35)" : "#fde68a"}`, borderRadius: 10, padding: "2px 9px", whiteSpace: "nowrap" }}>
                        Previously Updated
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: ink2, marginTop: 2 }}>
                    Avg position <strong style={{ color: red }}>{card.avgPosition}</strong> · {card.impressions.toLocaleString()} impressions
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0 }}>
                  {generated[card.fixId] && (
                    <button
                      onClick={() => setEditor(generated[card.fixId])}
                      title={done ? "Published — view the content (read-only)" : "View the generated content, then publish"}
                      style={{
                        padding: "7px 14px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif",
                        fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", whiteSpace: "nowrap",
                        background: done ? "#94a3b8" : "#16a34a",
                      }}
                    >
                      View Page Content
                    </button>
                  )}
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11.5, color: isDark ? "#cbd5e1" : "#334155", fontWeight: 600, whiteSpace: "nowrap" }} title="Default number of times each keyword is woven into the page (1–10). Override any keyword individually in the list below.">
                    Keyword Frequency
                    <input
                      type="number" min={1} max={10} value={card.density}
                      onChange={e => setDensity(card.fixId, parseInt(e.target.value, 10))}
                      style={{ width: 58, padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a" }}
                    />
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                  <div className="lhk-ac">
                    <button
                      className="lhk-reset"
                      onClick={() => props.onReset(card.fixId)}
                      title="Reset status" aria-label="Reset status"
                    >↺</button>
                    {(() => {
                      const working = busy.has(card.fixId);
                      // Green View button present (content generated, card not done/ignored) → the
                      // Approve button becomes "Regenerate" and re-runs the crawl for that card.
                      const canRegen = !!generated[card.fixId] && !done && !ignored;
                      return (
                        <button
                          className="lhk-approve"
                          onClick={e => {
                            const anchor = e.currentTarget;
                            const alreadyPublished = inserted.has(normalizePage(card.page));
                            // Already published to → warn once per session (via Approve OR Regenerate)
                            // that another round is additive. After the user confirms, don't ask again.
                            if (alreadyPublished && !reoptimizeAckedRef.current.has(card.fixId)) {
                              confirmDeletePopover(anchor, () => {
                                reoptimizeAckedRef.current.add(card.fixId);
                                void handleApprove(card, true);
                              }, canRegen ? REOPTIMIZE_MSG : REINSERT_MSG, "Continue", true);
                            } else if (canRegen) {
                              // Regenerate: acknowledged already-published card → go straight through;
                              // an unpublished generated card → light confirm.
                              if (alreadyPublished) void handleApprove(card, true);
                              else confirmDeletePopover(anchor, () => void handleApprove(card, true), "Regenerate this content?", "Regenerate");
                            } else {
                              // First-time Approve on a never-published page → standard modal confirm.
                              void handleApprove(card);
                            }
                          }}
                          disabled={working || done || card.keywords.length === 0}
                          // While crawling, paint it the Run Crawl orange (override the disabled fade).
                          style={working ? { background: "linear-gradient(135deg,#ea580c,#f97316)", borderColor: "#ea580c", color: "#fff", opacity: 1, cursor: "default" } : undefined}
                        >
                          {working ? "⏳ Working…" : canRegen ? "Regenerate" : "Approve"}
                        </button>
                      );
                    })()}
                    <label className="lhk-ignore">
                      <input type="checkbox" checked={ignored} onChange={e => props.onIgnore(card.fixId, e.target.checked)} />
                      <span>Ignore</span>
                    </label>
                    <label className="lhk-complete">
                      <input type="checkbox" checked={done} onChange={e => props.onDone(card.fixId, e.target.checked)} />
                      <span>Complete ✓</span>
                    </label>
                  </div>
                  {progress[card.fixId] && (
                    <div style={{ fontSize: 11, color: ink2, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                      <span className="lsg-spinner" />{progress[card.fixId]}
                    </div>
                  )}
                  </div>
                  <button
                    onClick={e => confirmDeletePopover(e.currentTarget, () => removeCard(card.fixId), "Remove this page?")}
                    title="Remove this page" aria-label="Remove this page"
                    style={{ background: "none", border: "none", cursor: "pointer", color: red, padding: 2, lineHeight: 0 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>
              </div>

              <div style={{ padding: "14px 16px" }}>
              {/* Non-Branded Low-Hanging Keywords box */}
              <div style={{ border: `1px solid ${boxBorder}`, borderRadius: 8, background: boxBg, padding: "10px 12px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: isDark ? "#aeb9cc" : "#475569", marginBottom: 8 }}>Non-Branded Low-Hanging Keywords</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center" }}>
                  {card.keywords.map(k => (
                    <span
                      key={k.query}
                      data-tip={k.manual ? "Added manually" : `Position ${k.position} · ${k.impressions} impressions`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: k.manual ? (isDark ? "#7c4a12" : "#fdba74") : chipBg, border: `1px solid ${k.manual ? (isDark ? "#b45309" : "#fdba74") : chipBorder}`, borderRadius: 14, padding: "4px 6px 4px 10px", fontSize: 12, color: isDark ? (k.manual ? "#fde68a" : "#dbe3ee") : "#0f172a" }}
                    >
                      {k.query}
                      {!k.manual && typeof k.position === "number" && (
                        <>
                          <span style={{ fontSize: 10, fontWeight: 600, color: link }}>Avg. Pos. #{k.position}</span>
                          <span style={{ fontSize: 10, color: inkMuted }}>·</span>
                        </>
                      )}
                      {/* Per-keyword insertion frequency (1–10); defaults to the card's Keyword Frequency. */}
                      <span title="How many times to weave this keyword in (1–10)" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", letterSpacing: .2 }}>KW Freq.</span>
                        <input
                          type="number" min={1} max={10} value={kwFreq(k, card)}
                          onChange={e => setKeywordFreq(card.fixId, k.query, parseInt(e.target.value, 10))}
                          aria-label={`Insertion frequency for ${k.query}`}
                          style={{ width: 36, padding: "1px 3px", border: "1px solid #16a34a", borderRadius: 6, fontSize: 11, fontWeight: 700, textAlign: "center", fontFamily: "'Inter','Mulish',sans-serif", color: "#15803d", background: "#f0fdf4" }}
                        />
                      </span>
                      <button
                        onClick={() => removeKeyword(card.fixId, k.query)}
                        aria-label={`Remove ${k.query}`}
                        style={{ background: "none", border: "none", cursor: "pointer", color: inkMuted, fontSize: 14, lineHeight: 1, padding: "0 2px" }}
                      >×</button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={addKw[card.fixId] ?? ""}
                    onChange={e => setAddKw(prev => ({ ...prev, [card.fixId]: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKeyword(card.fixId, addKw[card.fixId] ?? ""); } }}
                    placeholder="+ Add keyword (Enter)"
                    aria-label="Add a keyword to insert"
                    style={{ minWidth: 150, flex: "0 0 auto", padding: "4px 10px", border: "1px dashed #cbd5e1", borderRadius: 14, fontSize: 12, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none" }}
                  />
                </div>
                <div style={{ fontSize: 10, color: inkMuted, marginTop: 22 }}>Keywords with an <span style={{ background: isDark ? "#7c4a12" : "#fdba74", color: isDark ? "#fde68a" : "#7c2d12", fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>orange background</span> were added manually.</div>
              </div>
              </div>

            </div>
          );
        })}
      </div>

      {/* ── History — Optimized Pages ── */}
      <div style={{ padding: "0 9px", marginTop: 26 }}>
        <div className="cd-section cd-history-section">
          <div className="cd-section-hd">
            <span className="cd-hd-brand">History - Optimized Pages</span>
            <span className="audit-tip" style={{ marginLeft: 2 }}>
              <span className="audit-tip-icon">i</span>
              <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
                {"Every page that has had low-hanging keywords woven into its content via this tool, with the date it was last optimized."}
              </span>
            </span>
            <button className="cd-refresh-btn" onClick={handleRefresh} disabled={historyLoading}>
              {historyLoading ? "Loading…" : "↻ Refresh"}
            </button>
          </div>
          {!props.siteUrl ? (
            <p className="cd-help">Connect a website to see the pages this tool has optimized.</p>
          ) : history.length === 0 ? (
            <p className="cd-help">{historyLoaded ? "No pages have been optimized with this tool yet." : "Loading…"}</p>
          ) : (
            <table className="cd-history">
              <thead><tr><th>Title</th><th>URL</th><th>Page Type</th><th>Edited Date</th><th>All Keywords Added</th><th>Search Console</th></tr></thead>
              <tbody>
                {history.map(p => (
                  <tr key={p.url}>
                    <td>{p.title || "(untitled)"}</td>
                    <td><a href={p.url} target="_blank" rel="noopener noreferrer">{urlPath(p.url)}</a></td>
                    <td style={{ textTransform: "capitalize" }}>{p.type === "posts" ? "Post" : p.type === "pages" ? "Page" : "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{fmtDate(p.editedAt)}</td>
                    <td>
                      {p.keywords.length ? (
                        <a
                          href="#"
                          onClick={e => { e.preventDefault(); infoPopover(e.currentTarget, `${p.keywords.length} keyword${p.keywords.length === 1 ? "" : "s"} added`, p.keywords.join(", ")); }}
                        >View</a>
                      ) : (
                        <span style={{ color: inkMuted }}>—</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {gscProperty ? (
                        <a href={gscPerformanceUrl(gscProperty, p.url)} target="_blank" rel="noopener noreferrer" title="Open this page's last-6-months performance (clicks, impressions, avg. position) in Google Search Console">Open&nbsp;↗</a>
                      ) : (
                        <span style={{ color: inkMuted }} title="Connect this site to Google Search Console to enable this link">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <KeywordEditorDialog
        open={!!editor}
        html={editor?.html ?? ""}
        pageLabel={editor ? urlPath(editor.page) : ""}
        elementor={!!editor?.elementor}
        readOnly={!!editor && props.state[editor.fixId] === "done"}
        onClose={() => setEditor(null)}
        onPublish={publishEdited}
      />
    </div>
  );
}
